import { Component, Inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormText, FormBuilder, FormGroup } from "@coso/forms";
import { LoadingService } from "@coso/loading";
import { IS_SAAS } from "@coso/utility";
import { forkJoin } from "rxjs";
import { IConfigurationCreateOrUpdateDto } from "../../../models/auto-generated";
import { AUserPermissionsService } from "../../../services/a-user-permissions.service";
import { ConfigurationService } from "../../../services/configuration.service";
import { handleError } from "../../../utils/handle-error";
import { Validators } from "@angular/forms";

@Component({
  standalone:false,
  templateUrl: "./edit-configuration.component.html",
  styleUrls: ["./edit-configuration.component.scss"]
})
export class EditConfigurationComponent implements OnInit {
  instanceId: string;
  formGroup: FormGroup<IConfigurationCreateOrUpdateDto>;
  formText: FormText<IConfigurationCreateOrUpdateDto> = {
    name: {
      label: "Name",
      placeholder: "configuration name",
      popover: "The name is just a field which appears later in the list of configurations. By giving the configuration a meaningful name, it is easier to find it later in the list.",
      errors: {
        required: "The Name field is required.",
        maxlength: "The Name field must be at max 256 characters long."
      }
    },
    description: {
      label: "Description",
      placeholder: "description",
      popover: "A short description of the configuration. This field is optional.",
      errors: {
        maxlength: "The Name field must be at max 256 characters long."
      }
    }
  };
  isReadOnly: boolean;
  errorMessage: string;

  get title() {
    if (this.isReadOnly) {
      return `View configuration details`;
    } else {
      return `Edit configuration`;
    }
  }

  constructor(
    @Inject(IS_SAAS)
    public isSaaS: boolean,
    public loadingService: LoadingService,
    private route: ActivatedRoute,
    private router: Router,
    private configurationService: ConfigurationService,
    private userPermissionsService: AUserPermissionsService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.formGroup = this.fb.group<IConfigurationCreateOrUpdateDto>({
      id: this.fb.control(undefined),
      name: this.fb.control(undefined, [Validators.required, Validators.maxLength(256)]),
      description: this.fb.control(undefined, [Validators.maxLength(256)])
    });
    this.instanceId = this.route.snapshot.paramMap.get("instance-id");
    forkJoin([this.userPermissionsService.hasLimitedAccess$, this.userPermissionsService.isCustomerAgent$]).subscribe(
      ([hasLimitedAccess, isCustomerAgent]) => (this.isReadOnly = hasLimitedAccess || isCustomerAgent)
    );
    this.loadConfiguration();
  }

  returnToList(message?: string) {
    this.router.navigate(["../"], {
      relativeTo: this.route.parent,
      state: { message }
    });
  }

  save() {
    if (this.isReadOnly) return;

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
    } else {
      this.loadingService.start(`Saving configuration`, "saveConfiguration");
      this.configurationService.update(this.instanceId, this.formGroup.getRawValue()).subscribe(
        () => {
          this.returnToList(`Configuration '${this.formGroup.value.name}' has been updated successfully.`);
          this.loadingService.stop("saveConfiguration");
        },
        (error) => {
          this.errorMessage = `Failed to update configuration due to: ${handleError(error)}`;
          this.loadingService.stop("saveConfiguration");
        }
      );
    }
  }

  private loadConfiguration() {
    this.loadingService.start(`Loading configuration data`, "loadingConfiguration");
    this.configurationService.getById(this.instanceId).subscribe(
      (data) => {
        this.formGroup.patchValue(data);
        this.loadingService.stop("loadingConfiguration");
      },
      (error) => {
        this.errorMessage = `Failed to get configuration data due to: ${handleError(error)}`;
        this.loadingService.stop("loadingConfiguration");
      }
    );
  }
}
